import React, { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	TextField,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	CircularProgress,
	IconButton,
	Typography,
	Box,
	InputAdornment,
	Skeleton,
	ListItemAvatar,
	Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import DescriptionIcon from "@mui/icons-material/Description";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { useTheme } from "@mui/material/styles";
import { fetchCustomers } from "@/hooks/customer";
import { fetchContracts } from "@/hooks/contract";
import { fetchInvoices } from "@/hooks/invoice";

interface ShareRecordDialogProps {
	open: boolean;
	onClose: () => void;
	type: "customer" | "contract" | "invoice";
	onSelect: (id: string, label: string) => void;
}

export const ShareRecordDialog: React.FC<ShareRecordDialogProps> = ({
	open,
	onClose,
	type,
	onSelect,
}) => {
	const theme = useTheme();
	const [search, setSearch] = useState("");
	const [loading, setLoading] = useState(false);
	const [results, setResults] = useState<any[]>([]);

	useEffect(() => {
		if (!open) {
			setSearch("");
			setResults([]);
			return;
		}

		const delayDebounceFn = setTimeout(async () => {
			setLoading(true);
			try {
				if (type === "customer") {
					const { customers } = await fetchCustomers({ search, page: 1, limit: 10 });
					setResults(customers || []);
				} else if (type === "contract") {
					const { contracts } = await fetchContracts({ search, page: 1, limit: 10 });
					setResults(contracts || []);
				} else if (type === "invoice") {
					const { invoices } = await fetchInvoices({ search, page: 0, limit: 10 });
					setResults(invoices || []);
				}
			} catch (error) {
				console.error("Failed to fetch records", error);
				setResults([]);
			} finally {
				setLoading(false);
			}
		}, 500);

		return () => clearTimeout(delayDebounceFn);
	}, [search, type, open]);

	const handleSelect = (item: any) => {
		let label = "";
		if (type === "customer") {
			label = item.type === "company" ? item.name : `${item.information?.firstName || item.firstName || ""} ${item.information?.lastName || item.lastName || ""}`.trim();
			if (!label) label = item.registrationNumber || item.information?.registrationNumber || item.id;
		} else if (type === "contract") {
			label = item.label || item.uid || item.contractNumber || item.id;
		} else if (type === "invoice") {
			label = item.uid || item.invoiceNumber || item.id;
		}

		onSelect(item.id, label);
		onClose();
	};

	return (
		<Dialog 
			open={open} 
			onClose={onClose} 
			fullWidth 
			maxWidth="sm"
			PaperProps={{
				sx: {
					borderRadius: 3,
					backgroundImage: "none",
					backgroundColor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 30, 0.85)' : 'rgba(255, 255, 255, 0.95)',
					backdropFilter: "blur(16px)",
					boxShadow: theme.shadows[16],
				}
			}}
		>
			<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
				<Typography variant="h6" fontWeight="600">
					{type === "customer"
						? "Харилцагч хуваалцах"
						: type === "contract"
							? "Гэрээ хуваалцах"
							: "Нэхэмжлэх хуваалцах"}
				</Typography>
				<IconButton onClick={onClose} size="small" sx={{ color: "text.secondary", backgroundColor: "action.hover" }}>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>
			<DialogContent sx={{ px: 2, pb: 2, pt: "8px !important" }}>
				<TextField
					fullWidth
					variant="outlined"
					placeholder="Хайх утга оруулна уу..."
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					sx={{ mb: 2 }}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon fontSize="small" color="action" />
							</InputAdornment>
						),
						sx: { 
							borderRadius: 2, 
							backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)',
							"& fieldset": { borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }
						}
					}}
				/>
				
				<List 
					sx={{ 
						minHeight: 250, 
						maxHeight: 350, 
						overflowY: "auto",
						"&::-webkit-scrollbar": { width: 6 },
						"&::-webkit-scrollbar-thumb": {
							backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)",
							borderRadius: 3,
							"&:hover": {
								backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.25)",
							},
						},
						px: 1,
					}}
				>
					{loading ? (
						Array.from({ length: 4 }).map((_, idx) => (
							<ListItem key={idx} disablePadding dense sx={{ mb: 0.5 }}>
								<ListItemButton sx={{ borderRadius: 2 }}>
									<ListItemAvatar>
										<Skeleton variant="circular" width={40} height={40} />
									</ListItemAvatar>
									<ListItemText
										primary={<Skeleton variant="text" width="50%" sx={{ mb: 0.5 }} />}
										secondary={<Skeleton variant="text" width="30%" />}
									/>
								</ListItemButton>
							</ListItem>
						))
					) : results.length === 0 ? (
						<Box sx={{ p: 4, textAlign: "center", color: "text.secondary", mt: 4 }}>
							<SearchIcon sx={{ fontSize: 48, opacity: 0.2, mb: 1 }} />
							<Typography variant="body2">Илэрц олдсонгүй</Typography>
						</Box>
					) : (
						results.map((item) => {
							let primary = "";
							let secondary = "";
							let icon = <PersonIcon />;
							let avatarColor = "#1976d2";
							let avatarBg = "rgba(25, 118, 210, 0.1)";

							if (type === "customer") {
								primary = item.type === "company" ? item.name : `${item.information?.firstName || item.firstName || ""} ${item.information?.lastName || item.lastName || ""}`.trim();
								if (!primary) primary = item.registrationNumber || item.information?.registrationNumber || item.id;
								secondary = item.registrationNumber || item.information?.registrationNumber || "";
							} else if (type === "contract") {
								primary = item.label || item.uid || item.contractNumber || item.id;
								secondary = `Гэрээ: ${item.uid || item.contractNumber || ""}`;
								icon = <DescriptionIcon />;
								avatarColor = "#9c27b0";
								avatarBg = "rgba(156, 39, 176, 0.1)";
							} else if (type === "invoice") {
								primary = `Нэхэмжлэх: ${item.uid || item.invoiceNumber || item.id}`;
								secondary = `Дүн: ${item.total || item.totalAmount || 0}₮`;
								icon = <ReceiptIcon />;
								avatarColor = "#ed6c02";
								avatarBg = "rgba(237, 108, 2, 0.1)";
							}

							return (
								<ListItem key={item.id} disablePadding dense sx={{ mb: 0.5 }}>
									<ListItemButton onClick={() => handleSelect(item)} sx={{ borderRadius: 2 }}>
										<ListItemAvatar>
											<Avatar sx={{ bgcolor: avatarBg, color: avatarColor, width: 40, height: 40 }}>
												{React.cloneElement(icon, { sx: { fontSize: 22 } })}
											</Avatar>
										</ListItemAvatar>
										<ListItemText 
											primary={<Typography variant="subtitle2" color="text.primary" fontWeight="600" noWrap>{primary}</Typography>} 
											secondary={<Typography variant="caption" color="text.secondary" noWrap>{secondary}</Typography>} 
										/>
									</ListItemButton>
								</ListItem>
							);
						})
					)}
				</List>
			</DialogContent>
		</Dialog>
	);
};
